import type { SiteMapFeature } from '../../tri-shared/types/db.ts';
import {
  SITE_MAP_POLYGON_FILL_OPACITY,
  siteMapFeatureColor,
  siteMapPageGeometry,
  layoutRectToPoints,
  siteMapSymbolSpec,
  SITE_MAP_DEFAULT_TITLE,
  SITE_MAP_STROKE_PATTERNS,
  siteMapStrokePattern,
} from '../types/site-map.ts';
import type { SiteMapExportConfig, SiteMapLegendItem, SiteMapStyleSpec, SiteMapSymbolSpec } from '../types/site-map.ts';

export type MapMeasure = (text: string, size: number, bold?: boolean) => number;
export type MapRect = { x: number; y: number; w: number; h: number };
type Paint = { fill?: string; stroke?: string; weight?: number; opacity?: number; dash?: number[] };
export type MapDrawing =
  | ({ kind: 'path'; path: string } & Paint)
  | ({ kind: 'rect'; x: number; y: number; w: number; h: number } & Paint)
  | ({ kind: 'circle'; x: number; y: number; r: number } & Paint)
  | { kind: 'text'; x: number; y: number; text: string; size: number; bold?: boolean; fill: string };
export const SITE_MAP_FONT = 'Site Map Noto Sans';
export function siteMapPathStyle(p: SiteMapStyleSpec) {
  return {
    color: siteMapFeatureColor(p),
    weight: 1,
    fillOpacity: p.featureType === 'property-boundary' ? 0 : SITE_MAP_POLYGON_FILL_OPACITY,
    dash: SITE_MAP_STROKE_PATTERNS[siteMapStrokePattern(p)].dash,
  };
}
const text = (x: number, y: number, value: string, size: number, fill = '#262626', bold = false): MapDrawing => ({
  kind: 'text',
  x,
  y,
  text: value,
  size,
  fill,
  bold,
});

export function siteMapSymbol(p: SiteMapSymbolSpec, x: number, y: number, measure: MapMeasure): MapDrawing[] {
  const fill = siteMapFeatureColor(p);
  if (p.storageLocationID)
    return [
      { kind: 'circle', x, y, r: 7, fill, stroke: '#ffffff', weight: 0.8 },
      {
        kind: 'path',
        path: `M ${x - 3} ${y + 3} V ${y - 1} L ${x} ${y - 4} L ${x + 3} ${y - 1} V ${y + 3} Z`,
        stroke: '#ffffff',
        weight: 0.8,
      },
    ];
  switch (p.featureType) {
    case 'chemical-location': {
      const label = String(p.chemicalNumber);
      const size = Math.min(7, 11 / Math.max(1, measure(label, 1, true)));
      return [
        { kind: 'circle', x, y, r: 7, fill, stroke: '#ffffff', weight: 0.8 },
        text(x - measure(label, size, true) / 2, y + size * 0.35, label, size, '#ffffff', true),
      ];
    }
    case 'entrance':
      return [
        { kind: 'rect', x: x - 5, y: y - 5, w: 10, h: 10, fill },
        { kind: 'path', path: `M ${x - 2} ${y + 3} V ${y - 3} H ${x + 2} V ${y + 3}`, stroke: '#ffffff', weight: 1 },
      ];
    case 'cylinder':
      return [
        { kind: 'rect', x: x - 4, y: y - 5, w: 8, h: 10, fill },
        {
          kind: 'path',
          path: `M ${x - 4} ${y - 3} Q ${x} ${y - 6} ${x + 4} ${y - 3} M ${x - 4} ${y + 3} Q ${x} ${y + 6} ${x + 4} ${y + 3}`,
          stroke: '#ffffff',
          weight: 0.6,
        },
      ];
    case 'tank':
      return [
        { kind: 'circle', x, y, r: 6, fill, stroke: '#ffffff', weight: 0.6 },
        { kind: 'path', path: `M ${x - 4} ${y} H ${x + 4} M ${x} ${y - 4} V ${y + 4}`, stroke: '#ffffff', weight: 0.8 },
      ];
    default:
      return [{ kind: 'circle', x, y, r: 3, fill }];
  }
}

export function wrapSiteMapText(value: string, width: number, size: number, measure: MapMeasure): string[] {
  const lines: string[] = [];
  for (const paragraph of value.split(/\r?\n/)) {
    let line = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      if (line && measure(line + ' ' + word, size) <= width) {
        line += ' ' + word;
        continue;
      }
      if (line) {
        lines.push(line);
        line = '';
      }
      for (const character of word) {
        if (line && measure(line + character, size) > width) {
          lines.push(line);
          line = '';
        }
        line += character;
      }
    }
    lines.push(line);
  }
  return lines.length ? lines : [''];
}

export function siteMapFeatureAnchor(geometry: SiteMapFeature['geometry']): [number, number] {
  if (geometry.type === 'Point') return geometry.coordinates;
  if (geometry.type === 'Polygon') {
    const ring = [...(geometry.coordinates[0] ?? [])];
    if (ring.length > 1 && ring[0]![0] === ring.at(-1)![0] && ring[0]![1] === ring.at(-1)![1]) ring.pop();
    return ring.length
      ? [ring.reduce((s, p) => s + p[0], 0) / ring.length, ring.reduce((s, p) => s + p[1], 0) / ring.length]
      : [0, 0];
  }
  const points = geometry.coordinates;
  const lengths = points.slice(1).map((p, i) => Math.hypot(p[0] - points[i]![0], p[1] - points[i]![1]));
  let remaining = lengths.reduce((s, length) => s + length, 0) / 2;
  for (let i = 0; i < lengths.length; i++) {
    const length = lengths[i]!;
    if (length > 0 && remaining <= length) {
      const a = points[i]!,
        b = points[i + 1]!,
        fraction = remaining / length;
      return [a[0] + (b[0] - a[0]) * fraction, a[1] + (b[1] - a[1]) * fraction];
    }
    remaining -= length;
  }
  return points[0] ?? [0, 0];
}

export function siteMapFeatureDrawing(
  features: readonly SiteMapFeature[],
  project: (lat: number, lng: number) => { x: number; y: number },
  measure: MapMeasure,
): MapDrawing[] {
  const commands: MapDrawing[] = [];
  const labels: MapDrawing[] = [];
  for (const feature of features) {
    const p = feature.properties;
    const style = siteMapPathStyle(p);
    if (p.label && (p.featureType !== 'chemical-location' || p.labelPosition)) {
      const [lng, lat] = siteMapFeatureAnchor(feature.geometry);
      const anchor = project(lat, lng);
      const labelAt = p.labelPosition ? project(p.labelPosition[1], p.labelPosition[0]) : anchor;
      const lines = wrapSiteMapText(p.label, 130, 6.5, measure);
      const w = Math.max(...lines.map((line) => measure(line, 6.5))) + 6,
        h = lines.length * 8 + 4;
      const x = !p.labelPosition && feature.geometry.type === 'Point' ? labelAt.x + 9 : labelAt.x - w / 2;
      const y = labelAt.y - h / 2;
      if (p.labelPosition)
        labels.push({
          kind: 'path',
          path: `M ${anchor.x} ${anchor.y} L ${labelAt.x} ${labelAt.y}`,
          stroke: style.color,
          weight: 0.75,
        });
      labels.push({ kind: 'rect', x, y, w, h, fill: '#ffffff', opacity: 0.95 });
      lines.forEach((line, i) => labels.push(text(x + 3, y + 8 + i * 8, line, 6.5, style.color)));
    }
    if (feature.geometry.type === 'Point') {
      const { x, y } = project(feature.geometry.coordinates[1], feature.geometry.coordinates[0]);
      commands.push(...siteMapSymbol(siteMapSymbolSpec(p), x, y, measure));
    } else {
      const rings = feature.geometry.type === 'Polygon' ? feature.geometry.coordinates : [feature.geometry.coordinates];
      const points = rings.map((ring) => ring.map(([lng, lat]) => project(lat, lng)));
      const path = points
        .filter((ring) => ring.length)
        .map(
          (ring) =>
            ring.map((p, index) => `${index ? 'L' : 'M'} ${p.x} ${p.y}`).join(' ') +
            (feature.geometry.type === 'Polygon' ? ' Z' : ''),
        )
        .join(' ');
      if (!path) continue;
      if (feature.geometry.type === 'Polygon' && style.fillOpacity > 0)
        commands.push({ kind: 'path', path, fill: style.color, opacity: style.fillOpacity });
      commands.push({ kind: 'path', path, stroke: style.color, weight: style.weight, dash: style.dash });
      if (p.featureType === 'evacuation-route') {
        const ring = points[0]!;
        for (let i = 1; i < ring.length; i++) {
          const a = ring[i - 1]!,
            b = ring[i]!,
            angle = Math.atan2(b.y - a.y, b.x - a.x);
          commands.push({
            kind: 'path',
            path: `M ${b.x} ${b.y} L ${b.x - 5 * Math.cos(angle - 0.5)} ${b.y - 5 * Math.sin(angle - 0.5)} L ${b.x - 5 * Math.cos(angle + 0.5)} ${b.y - 5 * Math.sin(angle + 0.5)} Z`,
            fill: style.color,
          });
        }
      }
    }
  }
  return [...commands, ...labels];
}

/**
 * The symbol drawn to the left of a legend entry's label. Each branch consumes
 * only what its entry kind carries, so the compiler proves the set is covered.
 */
function siteMapLegendSwatch(
  item: SiteMapLegendItem,
  x: number,
  y: number,
  size: number,
  swatch: number,
  measure: MapMeasure,
): MapDrawing[] {
  const { color } = item;
  const cx = x + swatch / 2,
    cy = y + size / 2;
  if (item.kind === 'chemical')
    return siteMapSymbol(
      { featureType: 'chemical-location', chemicalNumber: item.number, color, storageLocationID: null },
      cx,
      cy,
      measure,
    );
  if (item.kind === 'location' && item.geometryType === 'Point')
    return siteMapSymbol(
      { featureType: 'annotation', color, storageLocationID: item.storageLocationID },
      cx,
      cy,
      measure,
    );
  if (item.kind === 'vessel')
    return siteMapSymbol({ featureType: item.featureType, color, storageLocationID: null }, cx, cy, measure);
  if (item.featureType === 'tank' || item.featureType === 'cylinder' || item.featureType === 'entrance')
    return siteMapSymbol({ featureType: item.featureType, color, storageLocationID: null }, cx, cy, measure);
  if (item.featureType === 'pathway' || item.featureType === 'evacuation-route')
    return [
      {
        kind: 'path',
        path: `M ${x} ${cy} H ${x + swatch}`,
        stroke: color,
        weight: 1.2,
        dash: siteMapPathStyle({ featureType: item.featureType, color, strokePattern: item.strokePattern }).dash,
      },
    ];
  const box = { x, y: y - 1, w: swatch, h: Math.max(6, size) };
  const outline: MapDrawing = {
    kind: 'rect',
    ...box,
    stroke: color,
    weight: 1,
    dash: SITE_MAP_STROKE_PATTERNS[item.strokePattern].dash,
  };
  if (item.featureType === 'property-boundary') return [outline];
  return [{ kind: 'rect', ...box, fill: color, opacity: SITE_MAP_POLYGON_FILL_OPACITY }, outline];
}

export function siteMapLegendPages(
  items: readonly SiteMapLegendItem[],
  first: MapRect,
  continuation: MapRect,
  size: number,
  gap: number,
  measure: MapMeasure,
): MapDrawing[][] {
  const pages: MapDrawing[][] = [];
  let index = 0,
    pending: string[] | null = null;
  do {
    const rect = pages.length ? continuation : first;
    const commands: MapDrawing[] = [
      text(rect.x, rect.y + 11, pages.length ? 'LEGEND (CONTINUED)' : 'LEGEND', 9, '#262626', true),
      { kind: 'path', path: `M ${rect.x} ${rect.y} H ${rect.x + rect.w}`, stroke: '#aaaaaa', weight: 0.5 },
    ];
    const cols = Math.max(1, Math.floor(rect.w / 170)),
      colW = rect.w / cols,
      lineH = size * 1.35,
      swatch = Math.max(12, size * 1.6),
      indent = swatch + 5;
    let y = rect.y + 22;
    while (index < items.length && y + Math.max(lineH, swatch) <= rect.y + rect.h) {
      let rowHeight = 0;
      for (let col = 0; col < cols && index < items.length; col++) {
        const item = items[index]!,
          x = rect.x + col * colW;
        const lines: string[] = pending ?? wrapSiteMapText(item.label, Math.max(10, colW - indent - 8), size, measure);
        const room = Math.max(1, Math.floor((rect.y + rect.h - y) / lineH));
        // Keep an ordinary legend entry together; split only labels taller than a whole continuation page.
        const continuationLines = Math.floor((continuation.h - 22) / lineH);
        if (lines.length > room && lines.length <= continuationLines) {
          y = rect.y + rect.h;
          break;
        }
        const shown = lines.slice(0, room);
        commands.push(...siteMapLegendSwatch(item, x, y, size, swatch, measure));
        shown.forEach((line, n) => commands.push(text(x + indent, y + size + n * lineH, line, size)));
        rowHeight = Math.max(rowHeight, shown.length * lineH, swatch);
        if (shown.length < lines.length) {
          pending = lines.slice(shown.length);
          break;
        }
        pending = null;
        index++;
      }
      y += rowHeight + Math.max(1, 4 + gap);
    }
    pages.push(commands);
  } while (index < items.length);
  return pages;
}

export function siteMapPrintLayout(
  config: SiteMapExportConfig,
  items: readonly SiteMapLegendItem[],
  measure: MapMeasure,
  facility: { name: string; address: string; year: string | number },
) {
  const g = siteMapPageGeometry(config.orientation),
    l = config.layout;
  const header = layoutRectToPoints(l.header, g.PAGE_W, g.PAGE_H),
    map = layoutRectToPoints(l.map, g.PAGE_W, g.PAGE_H),
    legend = layoutRectToPoints(l.legend, g.PAGE_W, g.PAGE_H);
  const heading: MapDrawing[] = [];
  const title = config.title.trim() || SITE_MAP_DEFAULT_TITLE;
  const scale = Math.min(1, header.h / 70);
  const titleSize = Math.min(
    l.titleFontSize * scale,
    Math.max(1, (header.w - 80) / Math.max(1, measure(title, 1, true))),
  );
  heading.push(
    text(header.x, header.y + titleSize, title, titleSize, '#262626', true),
    text(
      header.x + header.w - 65,
      header.y + titleSize,
      `Year: ${facility.year}`,
      Math.min(10, titleSize),
      '#262626',
      true,
    ),
  );
  let y = header.y + titleSize + 13 * scale;
  for (const [value, size, bold] of [
    [facility.name, 10, true],
    [facility.address, 8, false],
    ['FOR EMERGENCY RESPONSE USE - NOT FOR PUBLIC DISTRIBUTION', 6, false],
  ] as const) {
    const fs = Math.min(size * scale, header.w / Math.max(1, measure(value, 1, bold)));
    heading.push(text(header.x, y, value, fs, '#444444', bold));
    y += 13 * scale;
  }
  heading.push({
    kind: 'path',
    path: `M ${header.x} ${header.y + header.h - 3} H ${header.x + header.w}`,
    stroke: '#aaaaaa',
    weight: 0.5,
  });
  const frame: MapDrawing[] = [{ kind: 'rect', ...map, stroke: '#666666', weight: 0.75 }];
  // Compass is inside the map so moving its box never pushes labels outside the page.
  frame.push(
    { kind: 'rect', x: map.x + map.w - 45, y: map.y + 5, w: 40, h: 42, fill: '#ffffff', opacity: 0.9 },
    text(map.x + map.w - 28, map.y + 15, 'N', 8, '#262626', true),
    text(map.x + map.w - 27, map.y + 42, 'S', 7),
    text(map.x + map.w - 40, map.y + 29, 'W', 7),
    text(map.x + map.w - 14, map.y + 29, 'E', 7),
    {
      kind: 'path',
      path: `M ${map.x + map.w - 25} ${map.y + 19} L ${map.x + map.w - 29} ${map.y + 32} L ${map.x + map.w - 25} ${map.y + 28} L ${map.x + map.w - 21} ${map.y + 32} Z`,
      fill: '#262626',
    },
  );
  const legends = siteMapLegendPages(
    items.filter((i) => i.visible),
    legend,
    { x: g.MARGIN, y: 90, w: g.PAGE_W - g.MARGIN * 2, h: g.PAGE_H - 130 },
    l.legendFontSize,
    l.legendRowSpacing,
    measure,
  );
  const footer = text(g.MARGIN, g.PAGE_H - 12, 'Imagery (c) Esri, Maxar, Earthstar Geographics.', 6, '#666666');
  const pages = legends.map((commands, index) =>
    index === 0
      ? [...heading, ...frame, ...commands, footer]
      : [
          text(g.MARGIN, 32, `${facility.name} - Site map legend`, 12, '#262626', true),
          text(g.MARGIN, 50, `Year: ${facility.year} | Page ${index + 1}`, 9),
          ...commands,
          footer,
        ],
  );
  return { width: g.PAGE_W, height: g.PAGE_H, map, pages };
}

const esc = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
export function siteMapDrawingSvg(commands: readonly MapDrawing[], width: number, height: number): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 ${width} ${height}">${commands
    .map((c) => {
      if (c.kind === 'text')
        return `<text x="${c.x}" y="${c.y}" font-family="${SITE_MAP_FONT}" font-size="${c.size}" font-weight="${c.bold ? 700 : 400}" fill="${c.fill}">${esc(c.text)}</text>`;
      const paint = `fill="${c.fill ?? 'none'}" stroke="${c.stroke ?? 'none'}" stroke-width="${c.weight ?? 0}" opacity="${c.opacity ?? 1}"${c.dash?.length ? ` stroke-dasharray="${c.dash.join(' ')}"` : ''}`;
      if (c.kind === 'path') return `<path d="${c.path}" ${paint}/>`;
      if (c.kind === 'circle') return `<circle cx="${c.x}" cy="${c.y}" r="${c.r}" ${paint}/>`;
      return `<rect x="${c.x}" y="${c.y}" width="${c.w}" height="${c.h}" ${paint}/>`;
    })
    .join('')}</svg>`;
}
